import './assets/bk-error-message.css';
import DomUtils from '../../../../modules/bk/bk-dom-utils';

const errorTexts = {
    default: 'An unknown error occurred. Please try again.',
    exception: 'Error occurred.',
    denied: 'Permission denied.',
};
const createDOMElement = DomUtils.createDOMElement;

/**
 * A custom web component for displaying error messages.
 * @extends HTMLElement
 */
class BkErrorMessage extends HTMLElement {
    #titles = {};
    #errors = {};

    connectedCallback() {
        this.#render();
        this.addEventListener('bk-error', this.#handleError.bind(this));
    }

    disconnectedCallback() {
        this.removeEventListener('bk-error', this.#handleError.bind(this));
    }

    #render() {
        this.classList.add('hide');
        const errorContainer = createDOMElement('div', {
            'data-bk-error': '',
            role: 'alert',
        });
        errorContainer.classList.add('bk-error-message');
        this.appendChild(errorContainer);
    }

    /**
     * Triggers an error event with the specified error.
     * @param {string} [error='Unknown error'] - The error to be dispatched (optional).
     * @param {string} [type='default'] - The type of the error (optional).
     */
    triggerError(error = 'Unknown error', type = 'default') {
        const errorEvent = new CustomEvent('bk-error', {
            detail: {
                type,
                error: new Error(error),
            },
        });
        this.dispatchEvent(errorEvent);
    }

    #handleError(e) {
        const errorContainer = this.querySelector('[data-bk-error]');
        if (!errorContainer) {
            return;
        }

        const type = e.detail?.type || 'default';
        const error = e.detail?.error;
        const title = errorTexts[type] || '[Unknown error]';

        this.classList.remove('hide');

        this.#addTitle(type, title);
        this.#addError(type, error);
        this.#getAllErrors();

        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'smooth',
        });
    }

    #addTitle(type, title) {
        if (!this.#titles[type]) {
            this.#titles[type] = title;
        }
    }

    #addError(type, error) {
        if (!this.#errors[type]) {
            this.#errors[type] = [];
        }
        if (error && error.message && !this.#errors[type].includes(error.message)) {
            this.#errors[type].push(error.message);
        }
    }

    #getAllErrors() {
        const errorContainer = this.querySelector('[data-bk-error]');
        errorContainer.textContent = '';

        for (const type in this.#titles) {
            this.#appendTitle(type);
            this.#appendErrors(type);
        }
    }

    #appendTitle(type) {
        if (this.#titles[type]) {
            const titleElement = createDOMElement('p', {}, 'bk-error-message__text', this.#titles[type]);
            const errorContainer = this.querySelector('[data-bk-error]');
            errorContainer.appendChild(titleElement);
        }
    }

    #appendErrors(type) {
        if (!this.#errors[type] || this.#errors[type].length === 0) {
            return;
        }

        const errorList = createDOMElement('ul', {}, 'bk-error-message__list');

        this.#errors[type].forEach((error) => {
            const listItem = createDOMElement('li', {}, 'bk-error-message__item', error);
            errorList.appendChild(listItem);
        });

        const errorContainer = this.querySelector('[data-bk-error]');
        errorContainer.appendChild(errorList);
    }
}

export default BkErrorMessage;
